import React, {
    useMemo,
    useState,
    useEffect,
    useCallback,
    useRef,
    useLayoutEffect,
} from 'react';
import { useNavigation } from 'react-router';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import type { FeaturedVideo } from '~/modules-rocco/featured-item/data-mappers/featured-item-to-video';
import { DirectoryGridSection } from './DirectoryGridSection';
import { DirectoryProductGridSkeleton } from './DirectoryProductGridSkeleton';
import { DirectorySkeletonSection } from './DirectorySkeletonSection';
import { FeaturedBanner } from '~/ui-rocco/pages/products-page/FeaturedBanner';
import { Button } from '@rocco/components/button';
import {
    getSectionsForPage,
    getLayoutForSection,
} from './hooks/useProductViewMore';
import { useSectionVirtualization } from './hooks/useSectionVirtualization';
import { ContentGatePanel } from '~/modules-rocco/content-gate/ContentGatePanel';
import type { ContentGateState } from '~/modules-rocco/content-gate/content-gate';

/**
 * Minimum products required to show featured banner and View More button.
 * Below this threshold, we use a simple grid layout.
 */
const MIN_PRODUCTS_FOR_FEATURED_BANNER = 14;

interface DirectoryProductGridProps {
    /**
     * All product tiles to display.
     * Products are automatically split into alternating A/B sections.
     */
    productTiles: TileViewModel<ProductTileViewModel>[];
    /**
     * Featured banner data to display between sections.
     */
    featuredBanner: FeaturedVideo | null;
    /**
     * Ad tile to render in the featured (row-span-2) slot of the first section.
     * When provided, replaces the product featured tile. Falls back to product image if absent.
     */
    featuredAdTile?: React.ReactNode;
    /**
     * Pre-rendered amplifier tiles to distribute across sections (1 per section).
     */
    amplifierTiles?: React.ReactNode[];
    /**
     * Pagination component to render at the bottom (for mobile/fallback).
     * @deprecated Use View More pattern on desktop instead.
     */
    pagination?: React.ReactNode;
    /**
     * Optional class name for additional styling.
     */
    className?: string;
    /**
     * Whether a View More fetch is currently in progress.
     */
    isLoading?: boolean;
    /**
     * Whether there are more products to load.
     */
    hasMore?: boolean;
    /**
     * Callback to trigger loading more products.
     */
    onLoadMore?: () => void;
    /**
     * Error message if View More fetch failed.
     */
    error?: string | null;
    /**
     * Whether filters are currently active.
     * When true, the featured banner is hidden (featured item may not match filters).
     */
    hasActiveFilters?: boolean;
    /**
     * Total number of products in this category (from server).
     * Used to display "Showing all X products" message when end of list is reached.
     */
    totalCount: number;
    /**
     * Deep-linking: First page that has loaded products (1-indexed).
     * Pages 1 through (firstLoadedPage - 1) will be rendered as skeletons.
     * Default: 1 (no skeletons, all products are loaded from page 1)
     */
    firstLoadedPage?: number;
    /**
     * Deep-linking: The page user navigated to (for scroll-to-section).
     * On mount, the grid will scroll to this page's first section.
     * Default: 1 (no scroll)
     */
    requestedPage?: number;
    /**
     * Deep-linking: Callback when a skeleton page becomes visible (for lazy-loading).
     * Called with the page number that needs to be loaded.
     */
    onLoadSkeletonPage?: (pageNumber: number) => void;
    /**
     * Deep-linking: Set of page numbers currently being loaded.
     * Used to show loading indicator on skeleton sections.
     */
    loadingPages?: Set<number>;
    /**
     * When true, skip the deep-link scroll-to-section on mount.
     * Used on POP (back/forward) navigation to let scroll restoration handle positioning.
     */
    skipScrollToSection?: boolean;
    /**
     * Whether products were restored from cache (POP navigation).
     * When true, virtualization is temporarily disabled so all sections are in
     * the DOM, ensuring correct page height for scroll position restoration.
     */
    isRestoredFromCache?: boolean;
    /**
     * Content gate for this request. When closed the grid ends at the tiles the
     * loader allowed, blurred out under the sign-in panel, and View More, the
     * end-of-list message and the pager are all replaced by it.
     */
    contentGate?: ContentGateState;
}

interface Section {
    products: TileViewModel<ProductTileViewModel>[];
    layout: 'A' | 'B';
    index: number;
}

/**
 * Directory Product Grid component for the products category page.
 *
 * Displays products in alternating A/B section layout with featured banner:
 * - Section 0: Layout A (featured on RIGHT)
 * - Featured Banner (full-width) - only after first section if 2+ sections
 * - Section 1: Layout B (featured on LEFT)
 * - Section 2: Layout A (featured on RIGHT)
 * - Section 3: Layout B (featured on LEFT)
 * - View More button (if hasMore)
 *
 * Desktop: 5 columns (4 regular + 1 featured spanning 2 rows)
 * Tablet: 4 columns (3 regular + 1 featured spanning 2 rows)
 *
 * The banner and View More button are hidden when:
 * - Less than 14 products total
 */
export const DirectoryProductGrid = React.memo(
    ({
        productTiles,
        featuredBanner,
        featuredAdTile,
        amplifierTiles,
        pagination,
        className,
        isLoading = false,
        hasMore = false,
        onLoadMore,
        error,
        hasActiveFilters = false,
        totalCount,
        firstLoadedPage = 1,
        requestedPage = 1,
        onLoadSkeletonPage,
        loadingPages,
        skipScrollToSection,
        isRestoredFromCache = false,
        contentGate,
    }: DirectoryProductGridProps) => {
        // Single useNavigation subscription for the entire grid.
        // Previously each DirectoryGridSection had its own useNavigation() call,
        // causing all N sections to re-render on every navigation state change.
        // Lifting it here means only one subscription, and React.memo on sections
        // prevents re-renders when pendingUrl doesn't change for a given section.
        const navigation = useNavigation();
        const pendingUrl =
            navigation.state === 'loading'
                ? (navigation.location?.pathname ?? null)
                : null;

        // Ensure productTiles is a valid array - memoized to prevent dependency changes
        const safeProductTiles = useMemo(
            () => productTiles ?? [],
            [productTiles],
        );

        // Ref for scroll-to-section on deep-link
        const targetSectionRef = useRef<HTMLDivElement>(null);
        const hasScrolledRef = useRef(false);

        // ARIA announcement for screen readers when products load
        const [announcement, setAnnouncement] = useState('');

        // Calculate skeleton pages to render (pages before firstLoadedPage)
        const skeletonPages = useMemo(() => {
            const pages: number[] = [];
            for (let page = 1; page < firstLoadedPage; page++) {
                pages.push(page);
            }
            return pages;
        }, [firstLoadedPage]);

        // Calculate the starting section index for loaded products
        // This is needed to correctly assign section indices when deep-linking
        // Every page has 3 sections (42 products = 14 per section × 3 sections)
        const startingSectionIndex = useMemo(() => {
            if (firstLoadedPage <= 1) return 0;
            return 3 * (firstLoadedPage - 1);
        }, [firstLoadedPage]);

        // Scroll to the requested page's section on mount (for deep-linking)
        useLayoutEffect(() => {
            if (
                requestedPage > 1 &&
                !hasScrolledRef.current &&
                !skipScrollToSection &&
                targetSectionRef.current
            ) {
                hasScrolledRef.current = true;
                // Double rAF: first frame lets React flush DOM mutations,
                // second ensures the browser completes a paint cycle before scrolling.
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        targetSectionRef.current?.scrollIntoView({
                            behavior: 'instant',
                            block: 'start',
                        });
                    });
                });
            }
        }, [requestedPage, safeProductTiles.length, skipScrollToSection]);

        // Announce when new products are loaded or end of list is reached
        useEffect(() => {
            if (!isLoading && safeProductTiles.length > 0) {
                // Announce when end of list is reached
                if (
                    !hasMore &&
                    safeProductTiles.length >= MIN_PRODUCTS_FOR_FEATURED_BANNER
                ) {
                    setAnnouncement(
                        `Showing all ${totalCount.toLocaleString()} products`,
                    );
                }
                // Clear announcement after a short delay
                const timer = setTimeout(() => setAnnouncement(''), 1000);
                return () => clearTimeout(timer);
            }
        }, [isLoading, safeProductTiles.length, hasMore, totalCount]);

        const hasFeaturedAd = !!featuredAdTile;
        const hasAmplifier = !!amplifierTiles?.length;

        // Split products into sections with per-section sizing.
        // Each section's product count accounts for extra non-product grid items
        // (ad tile, amplifier tile) so total grid items = multiple of 5 (full rows).
        const sections = useMemo<Section[]>(() => {
            const result: Section[] = [];
            let offset = 0;
            let layoutIndex = startingSectionIndex;

            while (offset < safeProductTiles.length) {
                const isFeaturedSection =
                    layoutIndex % 3 === 0 && hasFeaturedAd;
                // Amplifier goes to non-featured sections when ads exist, all sections when no ads
                const hasAmplifierInSection = hasFeaturedAd
                    ? layoutIndex % 3 !== 0 && hasAmplifier
                    : hasAmplifier;

                // Section size = grid columns (5) × desired rows (3) - extra non-product items
                // Featured sections: 13 products (ad occupies featured row-span-2 slot, 8 top + 5 bottom)
                // Sections with amplifier: 14 products + 1 amplifier = 15 = 3×5
                // Sections without extras: 15 products = 3×5
                const currentSectionSize = isFeaturedSection
                    ? 13
                    : hasAmplifierInSection
                      ? 14
                      : 15;

                const sectionProducts = safeProductTiles.slice(
                    offset,
                    offset + currentSectionSize,
                );

                if (sectionProducts.length > 0) {
                    result.push({
                        products: sectionProducts,
                        layout: getLayoutForSection(layoutIndex),
                        index: layoutIndex,
                    });
                }

                offset += currentSectionSize;
                layoutIndex++;
            }

            return result;
        }, [
            safeProductTiles,
            startingSectionIndex,
            hasFeaturedAd,
            hasAmplifier,
        ]);

        // Calculate which section to scroll to for deep-linking
        // Target the FIRST section of the requested page so the user sees the full page
        const targetSectionIndex = useMemo(() => {
            if (requestedPage <= 1) return 0;
            const pageSections = getSectionsForPage(requestedPage);
            return pageSections[0] ?? 0;
        }, [requestedPage]);

        // =========================================================================
        // Section Virtualization
        // =========================================================================

        // Calculate total sections (skeleton + loaded)
        const totalSections = useMemo(() => {
            // Skeleton sections
            const skeletonSectionCount = skeletonPages.reduce(
                (count, pageNum) => count + getSectionsForPage(pageNum).length,
                0,
            );
            // Loaded sections
            return skeletonSectionCount + sections.length;
        }, [skeletonPages, sections.length]);

        // Virtualization is disabled during initial deep-link scroll
        // and during POP navigation (isRestoredFromCache) to ensure all sections
        // are in the DOM for accurate scroll position restoration.
        const [virtualizationEnabled, setVirtualizationEnabled] = useState(
            requestedPage <= 1 && !isRestoredFromCache,
        );

        // Enable virtualization after initial scroll completes (for deep-linking)
        useEffect(() => {
            if (requestedPage > 1 && hasScrolledRef.current) {
                // Delay enabling virtualization to let scroll settle
                const timer = setTimeout(() => {
                    setVirtualizationEnabled(true);
                }, 500);
                return () => clearTimeout(timer);
            }
        }, [requestedPage, safeProductTiles.length]);

        // Re-enable virtualization after POP scroll restoration completes.
        useEffect(() => {
            if (!isRestoredFromCache || virtualizationEnabled) return;

            let frame = 0;
            let rafId: number;
            const tick = () => {
                frame++;
                if (frame >= 4) {
                    setVirtualizationEnabled(true);
                } else {
                    rafId = requestAnimationFrame(tick);
                }
            };
            rafId = requestAnimationFrame(tick);
            return () => cancelAnimationFrame(rafId);
        }, [isRestoredFromCache, virtualizationEnabled]);

        // Calculate skeleton section count — needed by both the virtualization hook
        // (alwaysRenderedCount) and the globalArrayIndex calculation for loaded sections.
        const skeletonSectionCount = useMemo(() => {
            return skeletonPages.reduce(
                (count, pageNum) => count + getSectionsForPage(pageNum).length,
                0,
            );
        }, [skeletonPages]);

        // Ref flag: set to true before View More to pin visibleRange.start
        // and prevent flash when new sections are appended below viewport.
        const lockStartRef = useRef(false);

        // Initialize virtualization hook
        const {
            visibleRange,
            registerSection,
            registerExtraHeight,
            registerSectionHeight,
            topSpacerHeight,
            bottomSpacerHeight,
        } = useSectionVirtualization({
            totalSections,
            // Skeleton sections are always rendered (not virtualized), so exclude
            // them from spacer calculations to avoid double-counting their heights.
            alwaysRenderedCount: skeletonSectionCount,
            bufferAbove: 1,
            bufferBelow: 1,
            minVisibleSections: 5,
            enabled:
                virtualizationEnabled &&
                safeProductTiles.length >= MIN_PRODUCTS_FOR_FEATURED_BANNER,
            lockStartRef,
        });

        // Map each skeleton page number → its first global section index.
        // Used to feed measured skeleton heights into the virtualization model.
        const skeletonPageFirstSectionIndex = useMemo(() => {
            const map = new Map<number, number>();
            let cumulative = 0;
            for (const pageNum of skeletonPages) {
                map.set(pageNum, cumulative);
                cumulative += getSectionsForPage(pageNum).length;
            }
            return map;
        }, [skeletonPages]);

        // Handler: when a skeleton page measures itself, register its height
        // against the correct virtual section indices in the virtualization model.
        // The full height goes to the first index; remaining indices get 0.
        const handleSkeletonHeightMeasured = useCallback(
            (
                firstSectionIndex: number,
                totalHeight: number,
                sectionCount: number,
            ) => {
                registerSectionHeight(firstSectionIndex, totalHeight);
                for (let i = 1; i < sectionCount; i++) {
                    registerSectionHeight(firstSectionIndex + i, 0);
                }
            },
            [registerSectionHeight],
        );

        // Don't virtualize skeleton sections - always render all of them
        // Skeletons are lightweight divs and need to exist in DOM for IntersectionObserver to work
        // This fixes the issue where skeletons wouldn't load when scrolling up from high page numbers
        const visibleSkeletonPages = skeletonPages;

        // Filter loaded sections by visible range using globalArrayIndex
        // The virtualization hook uses sequential indices (0, 1, 2...) but
        // section.index can be non-sequential when deep-linking (e.g., 5, 6, 7...)
        // globalArrayIndex = skeletonSectionCount + arrayIndex in sections array
        // Pre-compute arrayIndex on each section to avoid O(n²) indexOf in the render loop.
        const visibleSections = useMemo(() => {
            const indexed = sections.map((s, i) => ({
                ...s,
                arrayIndex: i,
            }));
            if (!virtualizationEnabled) return indexed;

            return indexed.filter(({ arrayIndex }) => {
                const globalArrayIndex = skeletonSectionCount + arrayIndex;
                return (
                    globalArrayIndex >= visibleRange.start &&
                    globalArrayIndex < visibleRange.end
                );
            });
        }, [
            sections,
            visibleRange,
            virtualizationEnabled,
            skeletonSectionCount,
        ]);

        // Handle scroll to top - must be before early return to satisfy React hooks rules
        const handleScrollToTop = useCallback(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, []);

        // Handle edge case: no products
        if (sections.length === 0) {
            return null;
        }

        // Determine visibility of banner and View More based on product count
        // Note: Featured banner is hidden when filters are active (featured item may not match filters)
        const showFeaturedBanner =
            !hasActiveFilters &&
            !!featuredBanner?.video?.videoCover &&
            safeProductTiles.length >= MIN_PRODUCTS_FOR_FEATURED_BANNER &&
            sections.length > 1;

        // An empty result set has no tail to blur, and no case for a sign-in
        // wall over nothing — the no-results state stands.
        const showContentGate =
            !!contentGate?.gated && safeProductTiles.length > 0;

        const showViewMore =
            !showContentGate &&
            hasMore &&
            safeProductTiles.length >= MIN_PRODUCTS_FOR_FEATURED_BANNER;

        // Show end-of-list message when all products are loaded
        const showEndOfList =
            !showContentGate &&
            !isLoading &&
            !hasMore &&
            safeProductTiles.length > 0;

        // Determine the next layout for skeleton (alternates with last section)
        const lastSection = sections[sections.length - 1];
        const nextLayout: 'A' | 'B' =
            lastSection && lastSection.layout === 'A' ? 'B' : 'A';

        // Handle View More click
        const handleLoadMore = () => {
            if (onLoadMore && !isLoading) {
                // Pin visibleRange.start to prevent flash when new sections append
                lockStartRef.current = true;
                setAnnouncement('Loading more products...');
                onLoadMore();
            }
        };

        return (
            <div
                className={className}
                aria-busy={isLoading}
                style={{ overflowAnchor: 'none' }}
            >
                {/* ARIA live region for screen reader announcements */}
                <div
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                    className="sr-only"
                >
                    {announcement}
                </div>

                {/* Skeleton sections (always rendered, not virtualized).
                    Must render BEFORE the top spacer so DOM order matches the
                    virtualization model's section indices (skeletons first, then
                    loaded sections). Placing them after the spacer would double-
                    count their heights — once in the spacer, once as DOM elements. */}
                {visibleSkeletonPages.map((pageNumber) => (
                    <DirectorySkeletonSection
                        key={`skeleton-page-${pageNumber}`}
                        pageNumber={pageNumber}
                        onVisible={onLoadSkeletonPage}
                        isLoading={loadingPages?.has(pageNumber)}
                        showFeaturedBanner={
                            pageNumber === 1 && !hasActiveFilters
                        }
                        className={pageNumber > 1 ? 'mt-20' : undefined}
                        firstSectionIndex={skeletonPageFirstSectionIndex.get(
                            pageNumber,
                        )}
                        onHeightMeasured={handleSkeletonHeightMeasured}
                    />
                ))}

                {/* Top spacer for virtualized LOADED sections above viewport.
                    Excludes always-rendered skeleton sections (handled by alwaysRenderedCount). */}
                {topSpacerHeight > 0 && (
                    <div
                        style={{ height: `${topSpacerHeight}px` }}
                        aria-hidden="true"
                    />
                )}

                {/* Render visible loaded sections with alternating A/B layouts */}
                {visibleSections.map((section, idx) => {
                    // Determine if this section should get the scroll-to ref
                    const isTargetSection =
                        section.index === targetSectionIndex;
                    // Add top padding if there's a top spacer, visible skeletons, or this isn't the first visible section
                    const needsTopMargin =
                        idx > 0 ||
                        visibleSkeletonPages.length > 0 ||
                        topSpacerHeight > 0;

                    // Use pre-computed arrayIndex for O(1) global index calculation
                    const globalArrayIndex =
                        skeletonSectionCount + section.arrayIndex;

                    return (
                        <React.Fragment key={`section-${section.index}`}>
                            <div
                                ref={(node) => {
                                    // Register section for virtualization tracking
                                    // Use globalArrayIndex for sequential indexing that matches visibleRange
                                    registerSection(globalArrayIndex, node);
                                    // Also set scroll-to ref if this is the target
                                    if (isTargetSection && node) {
                                        (
                                            targetSectionRef as React.MutableRefObject<HTMLDivElement | null>
                                        ).current = node;
                                    }
                                }}
                                // Use padding instead of margin on child to prevent margin collapse.
                                // offsetHeight includes padding but not collapsed child margins,
                                // so spacer heights will be accurate when this section is virtualized.
                                className={needsTopMargin ? 'pt-20' : undefined}
                            >
                                <DirectoryGridSection
                                    products={section.products}
                                    featuredAdTile={
                                        section.index % 3 === 0
                                            ? featuredAdTile
                                            : undefined
                                    }
                                    amplifierTile={
                                        hasFeaturedAd
                                            ? section.index % 3 !== 0
                                                ? amplifierTiles?.[
                                                      (section.index -
                                                          Math.floor(
                                                              section.index / 3,
                                                          ) -
                                                          1) %
                                                          (amplifierTiles?.length ||
                                                              1)
                                                  ]
                                                : undefined
                                            : amplifierTiles?.[
                                                  section.index %
                                                      (amplifierTiles?.length ||
                                                          1)
                                              ]
                                    }
                                    useFeaturedLayout={
                                        section.index % 3 === 0 && hasFeaturedAd
                                    }
                                    layout={section.layout}
                                    hasMore={hasMore}
                                    isLastSection={
                                        idx === visibleSections.length - 1
                                    }
                                    pendingUrl={pendingUrl}
                                    isLoading={isLoading}
                                />
                            </div>

                            {/* Featured Banner - only after first section (section index 0) */}
                            {section.index === 0 && showFeaturedBanner && (
                                <div
                                    className="pt-20"
                                    ref={(node) =>
                                        registerExtraHeight(
                                            globalArrayIndex,
                                            node,
                                        )
                                    }
                                >
                                    <FeaturedBanner
                                        banner={featuredBanner}
                                        section="Grid"
                                    />
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}

                {/* Bottom spacer for virtualized sections below viewport */}
                {bottomSpacerHeight > 0 && (
                    <div
                        style={{ height: `${bottomSpacerHeight}px` }}
                        aria-hidden="true"
                    />
                )}

                {/* Loading Skeleton - shown while fetching more products */}
                {isLoading && (
                    <DirectoryProductGridSkeleton
                        layout={nextLayout}
                        className="mt-20"
                    />
                )}

                {/* Error Message */}
                {error && (
                    <div
                        className="mt-10 text-center text-base text-red-600"
                        role="alert"
                    >
                        {error}
                    </div>
                )}

                {/* View More Button */}
                {showViewMore && onLoadMore && (
                    <div className="flex justify-center pt-20">
                        <Button
                            onClick={handleLoadMore}
                            disabled={isLoading}
                            color="dark"
                            size="larger"
                            className="min-w-[10.625rem] border border-black transition-colors duration-200 hover:bg-white hover:text-black"
                            aria-label="Load more products"
                        >
                            View More
                        </Button>
                    </div>
                )}

                {/* End of List - shown when all products are loaded */}
                {showEndOfList && (
                    <div className="flex flex-col items-center gap-6 pt-20">
                        <p className="text-base text-gray-600">
                            Showing all {totalCount.toLocaleString()} products
                        </p>
                        <Button
                            onClick={handleScrollToTop}
                            color="dark"
                            size="larger"
                            className="min-w-[10.625rem] border border-black transition-colors duration-200 hover:bg-white hover:text-black"
                            aria-label="Scroll back to top of page"
                        >
                            Back to Top
                        </Button>
                    </div>
                )}

                {showContentGate && contentGate && (
                    <ContentGatePanel
                        totalCount={totalCount}
                        visibleTiles={contentGate.visibleTiles}
                        entityNoun="products"
                    />
                )}

                {/* Pagination - shown when View More is not available (mobile or fallback) */}
                {pagination && !showViewMore && !showContentGate && (
                    <div className="pt-26.25 md:pt-20">{pagination}</div>
                )}
            </div>
        );
    },
);

DirectoryProductGrid.displayName = 'DirectoryProductGrid';
